import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Home, Recycle, Sparkles, Truck, ChevronLeft, ExternalLink, Mail, Package, Trash2 } from 'lucide-react';

const LOGO = '/images/atob-objektservice-logo-dark-bg.png';
const HERO_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/73c84d3f-4e42-4579-a2eb-11d730a48d79/images/7db6b490dd16a6228f96897724d9a6cd531467de95afb5e10860e5900f649236.png';

export default function AtoBObjektservice() {
  return (
    <main data-testid="atob-objektservice-page" className="pt-20">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[480px] overflow-hidden">
        <img src={HERO_IMAGE} alt="AtoB Objektservice Team bei der Arbeit" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0F1A2E]/70"></div>
        <div className="relative z-10 container-main h-full flex flex-col justify-end pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Link to="/unternehmen" className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm uppercase tracking-wider mb-6">
              <ChevronLeft size={16} /> Alle Unternehmen
            </Link>
            <img src={LOGO} alt="AtoB Objektservice GmbH" className="h-16 md:h-20 w-auto mb-6 brightness-0 invert" />
            <p className="text-lg md:text-xl text-white/80 max-w-xl leading-relaxed">
              Professionelle Entrümpelung und Entsorgung – schnell, sauber und umweltbewusst.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Über uns */}
      <section className="section-spacing bg-[#0A1220]">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="accent-line mb-6"></div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
                Über AtoB Objektservice
              </h2>
              <p className="text-white/70 leading-relaxed mb-6">
                AtoB Objektservice GmbH steht für professionelle und umweltbewusste Entrümpelung und 
                Entsorgung. Ob Haushaltsauflösung, gewerbliche Räumung oder Reinigungsservice – wir 
                übernehmen alle Arbeiten schnell, sauber und zuverlässig. Dabei legen wir besonderen 
                Wert auf fachgerechte Entsorgung und Recycling.
              </p>
              <p className="text-white/70 leading-relaxed mb-8">
                Als Teil der Basic United Holding GmbH profitieren wir von einem starken Netzwerk 
                und können auch komplexe Aufträge effizient und termingerecht abwickeln. Unser erfahrenes 
                Team sorgt dafür, dass Sie sich um nichts kümmern müssen.
              </p>
              <div className="p-6 bg-[#0F1A2E]/5 border-l-4 border-[#0F1A2E]">
                <p className="text-sm text-white/50 uppercase tracking-wider mb-1">Geschäftsführung</p>
                <p className="font-semibold text-white">Admir Basic & Alessandro Costantino</p>
                <p className="text-sm text-white/70">Geschäftsführer (Gesellschafter: Admir Basic)</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: Home, label: 'Haushaltsauflösungen', desc: 'Komplett und besenrein' },
                  { icon: Recycle, label: 'Umweltbewusst', desc: 'Fachgerechtes Recycling' },
                  { icon: Sparkles, label: 'Reinigung', desc: 'Endreinigung nach Räumung' },
                  { icon: Truck, label: 'Abtransport', desc: 'Inkl. Transport & Entsorgung' },
                ].map((item, i) => (
                  <div key={i} className="p-6 bg-[#0F1A2E] border border-[#1e3a5f]">
                    <item.icon size={24} className="text-[#F5A623] mb-4" />
                    <h4 className="font-semibold text-white text-sm mb-1">{item.label}</h4>
                    <p className="text-xs text-white/50">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leistungen */}
      <ServicesSection />

      {/* Ablauf */}
      <ProcessSection />

      {/* CTA */}
      <section className="bg-[#0F1A2E] py-20">
        <div className="container-main text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Entrümpelung nötig? Wir packen an.
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
              Kontaktieren Sie uns für eine kostenlose Besichtigung und ein unverbindliches Angebot.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="mailto:info@atob-objektservice.de" className="inline-flex items-center justify-center gap-2 bg-[#0A1220] text-[#0F1A2E] px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#F5A623] hover:text-white transition-colors">
                <Mail size={16} /> Anfrage senden
              </a>
              <a href="https://www.atob-objektservice.de" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 border border-white text-white px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#0A1220] hover:text-[#0F1A2E] transition-colors">
                <ExternalLink size={16} /> Website besuchen
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}

function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const services = [
    { icon: Home, title: 'Haushaltsauflösungen', desc: 'Komplette Räumung von Wohnungen und Häusern – besenrein und termingerecht. Wir kümmern uns um alles, von der Sichtung bis zur Endreinigung.', features: ['Wohnungsauflösungen', 'Nachlassräumungen', 'Seniorenumzüge', 'Besenreine Übergabe'] },
    { icon: Trash2, title: 'Entrümpelung', desc: 'Gewerbliche und private Entrümpelung jeder Größenordnung. Ob Keller, Dachboden oder ganze Gebäude – wir räumen professionell.', features: ['Kellerentrümpelung', 'Dachbodenräumung', 'Gewerberäumung', 'Gartenentsorgung'] },
    { icon: Recycle, title: 'Entsorgung & Recycling', desc: 'Fachgerechte und umweltbewusste Entsorgung aller Materialien. Wir trennen, recyceln und entsorgen nach den geltenden Vorschriften.', features: ['Sperrmüllentsorgung', 'Elektronikentsorgung', 'Sondermüll', 'Recycling-Quote maximiert'] },
    { icon: Sparkles, title: 'Reinigungsservice', desc: 'Professionelle Reinigung nach der Räumung. Ihr Objekt wird besenrein oder auf Wunsch auch gründlich gereinigt übergeben.', features: ['Grundreinigung', 'Baureinigung', 'Fensterreinigung', 'Desinfektionsreinigung'] },
    { icon: Truck, title: 'Transport & Umzüge', desc: 'Wir transportieren Ihre Gegenstände sicher an den gewünschten Ort – ob zur Spende, ins Lager oder an eine neue Adresse.', features: ['Möbeltransport', 'Spendenanlieferung', 'Lagerung', 'Umzugsservice'] },
    { icon: Package, title: 'Wertanrechnung', desc: 'Verwertbare Gegenstände rechnen wir auf den Auftragswert an. So reduzieren sich Ihre Kosten und Brauchbares findet neue Besitzer.', features: ['Wertgegenstände-Sichtung', 'Verrechnung mit Auftrag', 'Spenden-Organisation', 'Kostenoptimierung'] },
  ];

  return (
    <section ref={ref} className="section-spacing bg-[#0F1A2E]">
      <div className="container-main">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }} className="text-center mb-16">
          <div className="flex justify-center mb-6"><div className="accent-line"></div></div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">Unsere Leistungen</h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">Alles aus einer Hand – von der Räumung bis zur besenreinen Übergabe.</p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.08 * (i + 1) }}
              className="bg-[#0A1220] p-8 border border-[#1e3a5f] hover:shadow-xl hover:border-[#0F1A2E]/20 transition-all duration-500">
              <s.icon size={28} className="text-[#0F1A2E] mb-4" />
              <h3 className="font-heading text-lg font-semibold text-white mb-3">{s.title}</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-5">{s.desc}</p>
              <ul className="space-y-2">
                {s.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-white/70">
                    <CheckCircle size={14} className="text-[#F5A623] flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = [
    { number: '01', title: 'Kontaktaufnahme', desc: 'Sie rufen an oder schreiben uns. Wir besprechen Ihr Anliegen und vereinbaren einen Besichtigungstermin.' },
    { number: '02', title: 'Besichtigung & Angebot', desc: 'Wir besichtigen das Objekt vor Ort, nehmen den Umfang auf und erstellen Ihnen ein transparentes Festpreisangebot.' },
    { number: '03', title: 'Räumung & Entsorgung', desc: 'Unser Team führt die Räumung termingerecht durch. Alle Materialien werden fachgerecht entsorgt oder recycelt.' },
    { number: '04', title: 'Besenreine Übergabe', desc: 'Nach der Räumung übergeben wir das Objekt besenrein – auf Wunsch mit professioneller Endreinigung.' },
  ];

  return (
    <section ref={ref} className="section-spacing bg-[#0A1220]">
      <div className="container-main">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }} className="mb-12">
          <div className="accent-line mb-6"></div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">So läuft es ab</h2>
          <p className="text-lg text-white/70 max-w-2xl">In vier einfachen Schritten zum sauberen Ergebnis.</p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 * (i + 1) }}
              className="relative">
              <span className="text-5xl font-heading font-bold text-[#0F1A2E]/10">{s.number}</span>
              <h3 className="font-heading text-lg font-semibold text-white mt-2 mb-3">{s.title}</h3>
              <p className="text-sm text-white/70 leading-relaxed">{s.desc}</p>
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 right-0 w-8 h-0.5 bg-[#F5A623] translate-x-4"></div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
